/* ──────────────────────────────────────────────────────────────
 *  useExportExplore — Excel export for Explore Designs table
 *
 *  Generates an .xlsx file from filtered table data and triggers
 *  a Blob download in the browser. Uses exceljs (no ReDoS vuln).
 * ────────────────────────────────────────────────────────────── */

import { useCallback } from 'react';
import ExcelJS from 'exceljs';
import type { BushingType } from '@/types/bushing';
import type { SampleRow } from '@/types/sample';
import { getExploreColumns } from '@/features/explore/exploreColumns';
import { useInputStore } from '@/stores/useInputStore';

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function useExportExplore() {
  const sorString = useInputStore((s) => s.sorString);

  const exportToExcel = useCallback(
    async (rows: SampleRow[], bushingType: BushingType, bestSampleId: string | null = null) => {
      if (rows.length === 0) return;

      const columns = getExploreColumns(bushingType);
      const fields = columns.map((c) => c.field);

      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('Explore Designs');

      // Add header row
      sheet.columns = columns.map((col) => ({
        header: col.headerName,
        key: col.field,
        width: (col.width ?? 120) / 8,
      }));

      // Style header row
      const headerRow = sheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.commit();

      // Add data rows
      for (const row of rows) {
        const isBest = bestSampleId != null && row.id === bestSampleId;
        const rowData: Record<string, string | number> = {};
        fields.forEach((field, idx) => {
          const val = row[field];
          if (field === 'id') {
            rowData[field] = isBest ? `${String(val ?? '')} ★` : String(val ?? '');
          } else if (val == null) {
            rowData[field] = '';
          } else if (columns[idx].type === 'number') {
            const num = Number(val);
            rowData[field] = isNaN(num) ? '' : Math.round(num * 10) / 10;
          } else {
            rowData[field] = String(val);
          }
        });
        const excelRow = sheet.addRow(rowData);
        if (isBest) {
          excelRow.font = { bold: true };
          excelRow.eachCell((cell) => {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFFFF3CD' },
            };
          });
        }
        excelRow.commit();
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const filename = sorString
        ? `${sorString}_explore_designs.xlsx`
        : 'explore_designs.xlsx';

      downloadBlob(blob, filename);
    },
    [sorString],
  );

  return { exportToExcel };
}
